import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axiosInstance from './axiosInstance';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

const humanizeKey = (key = '') =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const csvEscape = (value) => {
  const raw = value === undefined || value === null ? '' : String(value);
  return `"${raw.replace(/"/g, '""')}"`;
};

const JobsRevenueUpload = ({
  title = 'Upload Jobs Revenue',
  description = 'Upload the jobs revenue CSV to upsert subcontracted and storage recovery sales fields in the jobs collection.',
  endpoint = '/api/v1/jobs/revenue',
  successMessageText = 'Jobs revenue uploaded successfully.',
  errorMessageFallback = 'Failed to upload jobs revenue.',
  downloadFilePrefix = 'jobs-revenue-results',
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: 'job_number', direction: 'asc' });

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv files up to the configured size limit are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setResponseData(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const summaryEntries = useMemo(() => {
    if (!responseData) return [];

    return Object.entries(responseData).filter(([key, value]) => {
      if (Array.isArray(value)) return false;
      if (value && typeof value === 'object') return false;
      return value !== undefined;
    });
  }, [responseData]);

  const detailColumns = useMemo(() => {
    const details = Array.isArray(responseData?.details) ? responseData.details : [];
    const preferredOrder = ['job_number', 'status', 'reason'];
    const discovered = new Set(preferredOrder);

    details.forEach((detail) => {
      Object.keys(detail || {}).forEach((key) => discovered.add(key));
    });

    return Array.from(discovered).filter((key) =>
      details.some((detail) => detail && Object.prototype.hasOwnProperty.call(detail, key))
    );
  }, [responseData]);

  const sortedDetails = useMemo(() => {
    const details = Array.isArray(responseData?.details) ? [...responseData.details] : [];
    const { column, direction } = sortConfig;
    if (!column) return details;

    const parseComparable = (value) => {
      if (value === undefined || value === null || value === '') return '';
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && String(value).trim() !== '') {
        return numericValue;
      }
      return String(value).toLowerCase();
    };

    details.sort((left, right) => {
      const leftValue = parseComparable(left?.[column]);
      const rightValue = parseComparable(right?.[column]);

      if (leftValue === rightValue) return 0;
      if (leftValue === '') return 1;
      if (rightValue === '') return -1;

      const comparison = leftValue > rightValue ? 1 : -1;
      return direction === 'asc' ? comparison : -comparison;
    });

    return details;
  }, [responseData, sortConfig]);

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a CSV file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');
    setResponseData(null);

    try {
      const { data } = await axiosInstance.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResponseData(data);
      setSortConfig({ column: 'job_number', direction: 'asc' });
      setSuccessMessage(successMessageText);
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      setErrorMessage(apiMessage || errorMessageFallback);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setResponseData(null);
    setSuccessMessage('');
    setErrorMessage('');
    setSortConfig({ column: 'job_number', direction: 'asc' });
  };

  const handleSort = (column) => {
    setSortConfig((current) => ({
      column,
      direction:
        current.column === column && current.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const handleDownloadCsv = () => {
    if (!sortedDetails.length || !detailColumns.length) return;

    const header = detailColumns.map((column) => csvEscape(humanizeKey(column))).join(',');
    const rows = sortedDetails.map((detail) =>
      detailColumns
        .map((column) => csvEscape(detail?.[column] ?? ''))
        .join(',')
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

    link.href = url;
    link.setAttribute('download', `${downloadFilePrefix}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const renderFilePreview = () => {
    if (!selectedFile) {
      return (
        <Typography variant="body2" color="textSecondary">
          Drag and drop a CSV file here, or click to browse. Only one file can be uploaded at a time.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {selectedFile.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {(selectedFile.size / 1024).toFixed(2)} KB
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        {title}
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        {description}
      </Typography>

      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            backgroundColor: '#f0f6ff',
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        {renderFilePreview()}
        {fileRejections.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            Unsupported file type. Please upload a CSV file.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          startIcon={isUploading ? <CircularProgress size={18} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Upload CSV'}
        </Button>
        {selectedFile && (
          <Button variant="text" color="secondary" onClick={handleClear} disabled={isUploading}>
            Clear File
          </Button>
        )}
      </Box>

      {responseData && (
        <Box sx={{ mt: 5, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Upload Summary
          </Typography>
          <Grid container spacing={2}>
            {summaryEntries.map(([key, value]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Paper sx={{ p: 2, height: '100%' }} elevation={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {humanizeKey(key)}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {String(value)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {Array.isArray(responseData.details) && responseData.details.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6">
                  Record Details
                </Typography>
                <Button variant="outlined" onClick={handleDownloadCsv}>
                  Download CSV
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                elevation={1}
                sx={{
                  maxHeight: 520,
                  overflow: 'auto',
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {detailColumns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            backgroundColor: 'grey.100',
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.column === column}
                            direction={sortConfig.column === column ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort(column)}
                          >
                            {humanizeKey(column)}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDetails.map((detail, index) => (
                      <TableRow
                        key={`${detail.job_number || 'unknown'}-${index}`}
                        hover
                      >
                        {detailColumns.map((column) => (
                          <TableCell
                            key={column}
                            sx={{
                              verticalAlign: 'top',
                              minWidth: column === 'reason' ? 220 : 140,
                              wordBreak: 'break-word',
                            }}
                          >
                            {detail?.[column] !== undefined && detail?.[column] !== null
                              ? String(detail[column])
                              : '—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobsRevenueUpload;
